// client/src/components/leads/b2c/CustomerDetail.js
// FINAL VERSION: Edit/Delete buttons only show in Info tab (not in header)

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { customerAPI } from '../../../services/customerAPI';
import VisitHistory from './VisitHistory';
import CommunicationPanel from './CommunicationPanel';

export default function CustomerDetail({ customer: initialCustomer, onClose, onUpdate, onDelete }) {
  const [customer, setCustomer] = useState(initialCustomer);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [showAddVisit, setShowAddVisit] = useState(false);
  
  const [formData, setFormData] = useState({
    name: customer.name || '',
    phone: customer.phone || '',
    email: customer.email || '',
    source: customer.source || 'instagram',
    notes: customer.notes || ''
  });

  const [visitData, setVisitData] = useState({
    service: '',
    amount: '',
    visitDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Function to refresh customer data from backend
  const refreshCustomer = async () => {
    try {
      const response = await customerAPI.getById(customer.id);
      if (response.success && response.data) {
        setCustomer(response.data);
        if (onUpdate) await onUpdate();
      }
    } catch (error) {
      console.error('Error refreshing customer:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await customerAPI.update(customer.id, formData);
      if (response.success) {
        await refreshCustomer();
        setIsEditing(false);
      } else {
        alert(response.error || 'Failed to update customer');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this customer? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await customerAPI.delete(customer.id);
      if (response.success) {
        onDelete();
        onClose();
      } else {
        alert(response.error || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVisit = async (e) => {
    e.preventDefault();
    
    if (!visitData.service || !visitData.amount) {
      alert('Please enter service and amount');
      return;
    }

    setLoading(true);
    
    try {
      const response = await customerAPI.createVisit(customer.id, {
        service: visitData.service,
        amount: parseFloat(visitData.amount),
        visitDate: visitData.visitDate,
        notes: visitData.notes
      });

      if (response.success) {
        setShowAddVisit(false);
        setVisitData({
          service: '',
          amount: '',
          visitDate: new Date().toISOString().split('T')[0],
          notes: ''
        });
        
        await refreshCustomer();
        setActiveTab('visits');
      } else {
        alert(response.error || 'Failed to add visit');
      }
    } catch (error) {
      console.error('Error adding visit:', error);
      alert('Failed to add visit');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = () => {
    if (!customer.email) {
      alert('This customer does not have an email address. Please add an email first.');
      return;
    }
    
    // Open default email client with customer's email
    window.location.href = `mailto:${customer.email}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceIcon = (source) => {
    const icons = {
      'instagram': '📷',
      'facebook': '👥',
      'linkedin': '💼',
      'referral': '🤝',
      'website': '🌐',
      'email': '✉️',
      'phone': '📞',
      'walkin': '🚶',
      'other': '📋'
    };
    return icons[source] || icons['other'];
  };

  const getSourceColor = (source) => {
    const colors = {
      'instagram': 'bg-pink-100 text-pink-700 border-pink-200',
      'facebook': 'bg-blue-100 text-blue-700 border-blue-200',
      'linkedin': 'bg-blue-100 text-blue-700 border-blue-200',
      'referral': 'bg-green-100 text-green-700 border-green-200',
      'website': 'bg-purple-100 text-purple-700 border-purple-200',
      'email': 'bg-gray-100 text-gray-700 border-gray-200',
      'phone': 'bg-gray-100 text-gray-700 border-gray-200',
      'walkin': 'bg-orange-100 text-orange-700 border-orange-200',
      'other': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[source] || colors['other'];
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-green-100 text-green-700 border-green-200',
      'contacted': 'bg-blue-100 text-blue-700 border-blue-200',
      'customer': 'bg-purple-100 text-purple-700 border-purple-200',
      'inactive': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[status] || colors['new'];
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Sliding Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 animate-slide-in overflow-y-auto">
        
        {/* Header - NO Edit/Delete buttons here */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
              <h2 className="text-xl font-semibold text-gray-900">
                {customer.name}
              </h2>
            </div>
          </div>

          {/* Only Info and Visits tabs */}
          <div className="flex gap-1 mt-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'info'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Info
            </button>
            <button
              onClick={() => setActiveTab('visits')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'visits'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Visits
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              
              {/* Customer Information */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
                  
                  {/* Edit/Delete buttons INSIDE Info panel */}
                  {!isEditing && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                  {isEditing && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700"
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                      <select
                        value={formData.source}
                        onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="referral">Referral</option>
                        <option value="website">Website</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="walkin">Walk-in</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Phone</span>
                      <span className="text-sm font-medium text-gray-900">
                        {customer.phone || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Email</span>
                      <span className="text-sm font-medium text-gray-900">
                        {customer.email || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Source</span>
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${getSourceColor(customer.source)}`}>
                        <span>{getSourceIcon(customer.source)}</span>
                        <span>{customer.source}</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Status</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(customer.status)}`}>
                        {customer.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Added</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(customer.createdAt)}
                      </span>
                    </div>
                    {customer.notes && (
                      <div className="pt-3">
                        <span className="text-sm text-gray-600 block mb-2">Notes</span>
                        <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">
                          {customer.notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Communication Panel */}
              <CommunicationPanel 
                customer={customer} 
                onSendEmail={handleSendEmail}
              />

            </div>
          )}

          {/* Visits Tab */}
          {activeTab === 'visits' && (
            <div className="space-y-6">
              
              {/* Add Visit Button */}
              {!showAddVisit && (
                <button
                  onClick={() => setShowAddVisit(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors border border-green-600"
                >
                  <span>+</span>
                  <span>Add Visit</span>
                </button>
              )}

              {/* Add Visit Form */}
              {showAddVisit && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Visit</h3>
                  
                  <form onSubmit={handleAddVisit} className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service/Product *
                      </label>
                      <input
                        type="text"
                        value={visitData.service}
                        onChange={(e) => setVisitData({ ...visitData, service: e.target.value })}
                        placeholder="e.g., Haircut, Consultation, Product X"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter any service or product name
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (£) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={visitData.amount}
                        onChange={(e) => setVisitData({ ...visitData, amount: e.target.value })}
                        placeholder="0.00"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Visit Date
                      </label>
                      <input
                        type="date"
                        value={visitData.visitDate}
                        onChange={(e) => setVisitData({ ...visitData, visitDate: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes (optional)
                      </label>
                      <textarea
                        value={visitData.notes}
                        onChange={(e) => setVisitData({ ...visitData, notes: e.target.value })}
                        placeholder="Any additional details..."
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddVisit(false);
                          setVisitData({
                            service: '',
                            amount: '',
                            visitDate: new Date().toISOString().split('T')[0],
                            notes: ''
                          });
                        }}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {loading ? 'Recording...' : 'Record Visit'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Visit History */}
              <VisitHistory 
                customerId={customer.id}
                onVisitDeleted={refreshCustomer}
              />
            </div>
          )}

        </div>

      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>,
    document.body
  );
}